import { cn } from "@utils/cn.ts";
import ShiftCode from "./ThShiftCodeLegend";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { PrevTailData } from "@/hooks/roster-craete/useRosterPrevTail";
import type { CellViolationInfo } from "@/services/validation";
import {
  selectVisibleViolationGroups,
  selectVisibleViolationMessages,
} from "@/services/validation";
import type { AssignmentStartInfo } from "@/services/assignment";
import AssignmentGroupBar from "@/pages/roster-create/components/shift-code/assignment-group-bar";
import ViolationGroupBarCode from "@/pages/roster-create/components/shift-code/violation-group-bar-code";
import {
  resolveShiftFromScheduleCodeFromMaps,
  type ShiftLookupMaps,
} from "@/utils/shift-util";
import type { ShiftCatalogShift } from "@/services/shift/shiftCatalog";

type PrevTailCellsProps = {
  readonly prevTailData?: PrevTailData | null;
  readonly nurseId: string;
  readonly nursesIndex: number;
  readonly isEditMode: boolean;
  readonly shiftLookupMaps: ShiftLookupMaps<ShiftCatalogShift>;
  readonly cellViolationMap?: Map<string, CellViolationInfo>;
  readonly assignmentStarts: readonly AssignmentStartInfo[];
  readonly showManpowerViolations: boolean;
  readonly showConfigViolations: boolean;
};

export default function PrevTailCells({
  prevTailData,
  nurseId,
  nursesIndex,
  isEditMode,
  shiftLookupMaps,
  cellViolationMap,
  assignmentStarts,
  showManpowerViolations,
  showConfigViolations,
}: PrevTailCellsProps) {
  if (!prevTailData?.nurses) return null;

  const prevNurse = prevTailData.nurses.find(
    (n) => String(n.nurse_id) === nurseId
  );
  const prevDaysSource =
    prevTailData.tail_days?.length > 0
      ? prevTailData.tail_days
      : prevNurse?.shifts
        ? Object.keys(prevNurse.shifts).map(Number)
        : [];
  const prevDays = prevDaysSource
    .map(Number)
    .filter((day) => day >= 1 && day <= 31)
    .sort((a, b) => a - b);

  if (prevDays.length === 0) return null;

  return (
    <div className="flex items-center  relative ">
      <div className="absolute inset-0 -top-[8px]  left-[1px] border-x border-dotted" />
      {prevDays.map((day, i) => {
        const shiftCode = prevNurse?.shifts?.[String(day)];
        const shiftMeta = resolveShiftFromScheduleCodeFromMaps(
          shiftCode,
          shiftLookupMaps
        );
        const tailIndex = i - prevDays.length;
        const cellKey = `${nurseId}-${tailIndex}`;
        const cellInfo = cellViolationMap?.get(cellKey);
        const isError = cellInfo?.isError ?? false;
        const violationGroups = cellInfo?.groups ?? [];

        const displayedViolationMessages = selectVisibleViolationMessages({
          typedMessages: cellInfo?.typedMessages,
          fallbackMessages: cellInfo?.messages,
          showManpowerViolations,
          showConfigViolations,
        });

        const visibleViolationGroups = selectVisibleViolationGroups(
          violationGroups,
          {
            showManpowerViolations,
            showConfigViolations,
          }
        );
        const assignmentStart = assignmentStarts.find(
          (start) => start.startDay === tailIndex
        );
        const cellCode = shiftMeta?.shift_id ?? shiftCode ?? "-";
        const cellColor = shiftMeta?.color ?? "#d6d6de";

        const cell = (
          <div
            className={cn(
              "opacity-70",
              isEditMode ? "cursor-not-allowed" : "cursor-default"
            )}
            data-nurse-id={nurseId}
            data-day={tailIndex + 1}
            data-is-error={isError ? "true" : "false"}
            data-default-shift={shiftMeta?.default_shift ?? ""}
            data-shift-gb={shiftMeta?.shift_gb ?? ""}
            style={
              displayedViolationMessages.length > 0
                ? { backgroundColor: cellColor }
                : undefined
            }
          >
            <ShiftCode code={cellCode} color={cellColor} />
          </div>
        );

        return (
          <div key={`prev-${day}`} data-column={`prev-day${day}`}>
            <div
              className="p-[3px] flex items-center relative"
              data-roster-shift-cell=""
            >
              {assignmentStart ? (
                <AssignmentGroupBar
                  key={`tail-asgn-${nursesIndex}-${tailIndex}-${assignmentStart.reason}`}
                  group={assignmentStart}
                />
              ) : null}

              {visibleViolationGroups.map((group, idx) => {
                if (!group.isStart) return null;

                const stableKey = `${nursesIndex}-${tailIndex}-${group.type}-${group.startDay}-${group.endDay}`;
                return (
                  <ViolationGroupBarCode
                    key={stableKey}
                    group={group}
                    className="left-[1px]"
                    style={{ zIndex: 10 + idx }}
                  />
                );
              })}

              {displayedViolationMessages.length > 0 ? (
                <Tooltip>
                  <TooltipTrigger asChild>{cell}</TooltipTrigger>
                  <TooltipContent className="bg-[#444444] text-white whitespace-nowrap">
                    <div className="space-y-1">
                      <p className="font-semibold text-sm mb-1">위반사항:</p>
                      {displayedViolationMessages.map((message, idx) => (
                        <p key={idx} className="text-xs">
                          {message}
                        </p>
                      ))}
                    </div>
                  </TooltipContent>
                </Tooltip>
              ) : (
                cell
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
